import React, { useEffect, useState } from "react";
import { arrayOf, bool, number, shape, string } from "prop-types";
import { getMomentTime, getMomentTimeFromShort, toDayStringLocale } from "utils";
import moment from "moment";
import styles from "./LoadingCalendar.module.styl";
import { Days } from "utils/days";
import cx from "classnames";
import TooltipWrapper from "uikit/components/Tooltip/TooltipWrapper";
import CalendarTooltipCard from "../calendarTooltipCard/calendarTooltipCard";
import CalendarItem from "../calendarItem/calendarItem";
import { percentageLevels } from "utils/indicator/indicatorSettings";
import { TrainingsGraphCalculator } from "../model";

export default function LoadingCalendar({ workingDays, dates, showCameraDetectionData }) {
    const [time, setTime] = useState(null);
    const [workingDaysRange, setWorkingDaysRange] = useState(null);
    const [trainingsGraph, setTrainingsGraph] = useState(null);
    const [activeDay, setActiveDay] = useState(1);

    const daysOfWeek = Days.GetAllDays();
    const calendarRowDuration = moment.duration(30, "minutes");

    useEffect(() => {
        const getWorkingDayTimeRange = function (item, minTrain) {
            if (item === null || item === undefined) {
                return [];
            }
            let result = [];

            let startTime = getMomentTime(item.startTime);
            let endTime = getMomentTime(item.endTime);

            if (endTime.isBefore(startTime)) {
                endTime = moment("23:30", "HH:mm");
            }

            if (startTime.isSame(endTime) && startTime.isSame(moment("00:00", "HH:mm"))) {
                endTime = moment("23:59", "HH:mm");
            }

            while (startTime <= endTime) {
                result.push(startTime.format("HH:mm"));
                startTime = startTime.add(minTrain, "ms");
            }
            return result;
        };

        const initialWorkingDaysRange = function (workingDays, daysOfWeek, minTrainingDuration) {
            setWorkingDaysRange(
                workingDays.map((x) => ({
                    workingDayId: daysOfWeek.find((i) => i.Id === x.day).Index,
                    timeRange: getWorkingDayTimeRange(x, minTrainingDuration),
                })),
            );
        };

        const initialTrainingsRange = function (
            workingDays,
            daysOfWeek,
            minTrainingDuration,
            time,
        ) {
            if (time === null || time.length === 0) {
                return [];
            }
            return workingDays.map((x) => ({
                workingDayId: daysOfWeek.find((i) => i.Id === x.day).Index,
                trainings: x.trainingData.map((y) => {
                    let trainingStartTime = getMomentTime(y.startTime);
                    let trainingEndTime = getMomentTime(y.endTime);

                    let prepTime = time.map((item) => getMomentTime(item));

                    let startValues = prepTime.filter((isoDate) =>
                        isoDate.isSameOrBefore(trainingStartTime),
                    );

                    let startValue = startValues[startValues.length - 1];

                    let endValue = prepTime.filter((isoDate) =>
                        isoDate.isSameOrAfter(trainingEndTime),
                    )[0];

                    if (endValue === undefined) {
                        endValue = trainingEndTime;
                    }

                    let result = [];
                    while (startValue.isBefore(endValue)) {
                        result.push(startValue.format("HH:mm"));
                        startValue = startValue.add(calendarRowDuration, "ms");
                    }

                    return {
                        trainingsData: y,
                        trainingsRange: result,
                        trainingsDate: x.date,
                        trainingStartTime: getMomentTime(y.startTime),
                        trainingEndTime: getMomentTime(y.endTime),
                    };
                }),
            }));
        };

        const initialTime = function () {
            if (workingDaysRange === null || workingDaysRange === undefined) {
                return [];
            }

            let minWorkingDayStartTime = workingDaysRange
                .map((x) => getMomentTimeFromShort(x.timeRange[0]))
                .reduce(function (a, b) {
                    return a < b ? a : b;
                });

            let maxWorkingDayEndTime = workingDaysRange
                .map((x) => getMomentTimeFromShort(x.timeRange[x.timeRange.length - 1]))
                .reduce(function (a, b) {
                    return a > b ? a : b;
                });

            let minTrainingStartTime,
                maxTrainingEndTime = null;

            if (
                workingDays.some(
                    (item) => item.trainingData !== null && item.trainingData.length > 0,
                )
            ) {
                minTrainingStartTime = workingDays
                    .flatMap((x) => x.trainingData.map((y) => getMomentTime(y.startTime)))
                    .reduce(function (a, b) {
                        return a < b ? a : b;
                    });

                maxTrainingEndTime = workingDays
                    .flatMap((x) => x.trainingData.map((y) => getMomentTime(y.endTime)))
                    .reduce(function (a, b) {
                        return a > b ? a : b;
                    });
            }

            let result = [];

            let minTime =
                minTrainingStartTime == null
                    ? minWorkingDayStartTime
                    : minTrainingStartTime < minWorkingDayStartTime
                    ? minTrainingStartTime
                    : minWorkingDayStartTime;

            let maxTime =
                maxTrainingEndTime == null
                    ? maxWorkingDayEndTime
                    : maxTrainingEndTime > maxWorkingDayEndTime
                    ? maxTrainingEndTime
                    : maxWorkingDayEndTime;

            while (minTime <= maxTime) {
                result.push(minTime.format("HH:mm"));
                minTime = minTime.add(calendarRowDuration, "ms");
            }
            setTime(result);
            return result;
        };

        initialWorkingDaysRange(workingDays, daysOfWeek, calendarRowDuration);
        let calculatedTime = initialTime();
        if (
            workingDays.some((item) => item.trainingData !== null && item.trainingData.length > 0)
        ) {
            let trainingsRange = initialTrainingsRange(
                workingDays,
                daysOfWeek,
                calendarRowDuration,
                calculatedTime,
            );

            if (trainingsRange !== null && time !== null) {
                setTrainingsGraph(TrainingsGraphCalculator.getGraph(trainingsRange, time));
            }
        }
    }, [calendarRowDuration, daysOfWeek, time, trainingsGraph, workingDays, workingDaysRange]);

    function checkTraining(dayIndex, time) {
        if (trainingsGraph === null) {
            return null;
        }

        let data = trainingsGraph.find((x) => x.workingDayId === dayIndex);
        if (data === undefined) {
            return null;
        }

        return data.trainingsGridData.filter((x) => x.startTime === time);
    }

    return (
        <div>
            <div className={styles.mobileSwitcher}>
                <div className={styles.mobileDaySwitcher}>
                    {dates.map((item, index) => (
                        <div key={index}>
                            <div className={cx(styles.dayName, !item.isWorkingDay && styles.red)}>
                                {item.dayOfWeek}
                            </div>
                            <div
                                className={cx(
                                    styles.date,
                                    index === activeDay - 1 && styles.active,
                                    !item.isWorkingDay && index !== activeDay - 1 && styles.red,
                                )}
                                onClick={() => setActiveDay(index + 1)}
                            >
                                <div className={styles.dayValue}>
                                    {toDayStringLocale(item.date)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={styles.summary}>
                    <div className={styles.summaryHeader}>
                        Посетителей:
                        <div className={styles.summaryValue}>
                            {dates[activeDay - 1].participantsCount}
                        </div>
                    </div>
                    <div className={styles.summaryHeader}>
                        Загрузка:
                        <div
                            className={cx(
                                styles.summaryValue,
                                percentageLevels.indicatorColorWithOverload(
                                    dates[activeDay - 1].loading,
                                ).textStyle,
                            )}
                        >
                            {dates[activeDay - 1].loading}%
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.calendar} data-test-name="loading_calendar">
                <div>
                    {time?.map((item, index) => (
                        <div key={index} className={styles.time}>
                            {item}
                        </div>
                    ))}
                </div>

                <div className={styles.blocks} data-test-name="loading_calendar_blocks">
                    {daysOfWeek.map((item) => (
                        <div key={item.Index} style={{ overflow: "hidden" }}>
                            {time?.map((innerItem, innerIndex) => (
                                <div
                                    key={innerIndex}
                                    className={cx(
                                        styles.block,
                                        workingDaysRange.find(
                                            (x) => x.workingDayId === item.Index,
                                        ) !== undefined
                                            ? workingDaysRange
                                                  .find((x) => x.workingDayId === item.Index)
                                                  .timeRange.includes(innerItem)
                                                ? styles.full
                                                : styles.empty
                                            : styles.empty,
                                    )}
                                >
                                    {checkTraining(item.Index, innerItem) !== null
                                        ? checkTraining(item.Index, innerItem).map(
                                              (training, trainingIndex) => (
                                                  <TooltipWrapper
                                                      key={trainingIndex}
                                                      padding={"none"}
                                                      placement={"right"}
                                                      overlayContent={
                                                          <CalendarTooltipCard
                                                              trainingData={training.data}
                                                              date={training.date}
                                                              showCameraDetectionData={
                                                                  showCameraDetectionData
                                                              }
                                                          />
                                                      }
                                                      trigger={"click"}
                                                  >
                                                      <div>
                                                          <CalendarItem
                                                              sportTypeName={
                                                                  training.data.sportTypeName
                                                              }
                                                              schoolName={training.data.schoolName}
                                                              loadingPercent={
                                                                  training.data.loadingPercent
                                                              }
                                                              groupCode={training.data.groupCode}
                                                              height={training.height}
                                                              position={training.position}
                                                              rowWidth={training.maxRowWidth}
                                                              offsetY={training.offsetY}
                                                              dataTestName={`loading_training_block_${training.data.trainingId}`}
                                                          />
                                                      </div>
                                                  </TooltipWrapper>
                                              ),
                                          )
                                        : null}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <div className={styles.mobileBlocks}>
                    <div>
                        {time?.map((innerItem, innerIndex) => (
                            <div
                                key={innerIndex}
                                className={cx(
                                    styles.block,
                                    workingDaysRange.find((x) => x.workingDayId === activeDay) !==
                                        undefined
                                        ? workingDaysRange
                                              .find((x) => x.workingDayId === activeDay)
                                              .timeRange.includes(innerItem)
                                            ? styles.full
                                            : styles.empty
                                        : styles.empty,
                                )}
                            >
                                {checkTraining(activeDay, innerItem) !== null
                                    ? checkTraining(activeDay, innerItem).map(
                                          (training, trainingIndex) => (
                                              <CalendarItem
                                                  key={trainingIndex}
                                                  sportTypeName={training.data.sportTypeName}
                                                  schoolName={training.data.schoolName}
                                                  loadingPercent={training.data.loadingPercent}
                                                  groupCode={training.data.groupCode}
                                                  height={training.height}
                                                  position={training.position}
                                                  rowWidth={training.maxRowWidth}
                                                  offsetY={training.offsetY}
                                                  dataTestName={`loading_training_block_mobile_${training.data.trainingId}`}
                                              />
                                          ),
                                      )
                                    : null}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

LoadingCalendar.propTypes = {
    showCameraDetectionData: bool.isRequired,
    workingDays: arrayOf(
        shape({
            day: string,
            startTime: string,
            endTime: string,
            loading: number,
            date: string,
            participantsCount: number,
            trainingData: arrayOf(
                shape({
                    trainingId: number,
                    endTime: string,
                    groupCode: string,
                    loadingPercent: number,
                    participantsCount: number,
                    schoolName: string,
                    sportTrainingLevel: string,
                    sportTypeName: string,
                    sportsmanCount: number,
                    startTime: string,
                    coachName: string,
                    sportsmenCountByCamera: number,
                }),
            ),
        }),
    ).isRequired,
    dates: arrayOf(
        shape({
            day: string,
            dayOfWeek: string,
            participantsCount: number,
            loading: number,
        }),
    ).isRequired,
};
